import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Lock, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react'
import { http } from '@/services/api'
import { DiamondIcon, GoldText, LuxuryButton } from '@/components/UI'

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token')
    }
  }, [token])

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters long'
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter'
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter'
    }
    if (!/\d/.test(pwd)) {
      return 'Password must contain at least one number'
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)) {
      return 'Password must contain at least one special character'
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!token) {
      setError('Invalid or missing reset token')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    const validationError = validatePassword(password)
    if (validationError) {
      setError(validationError)
      return
    }

    setIsLoading(true)

    try {
      await http('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      })
      setSuccess(true)
      setTimeout(() => {
        navigate('/login')
      }, 3000)
    } catch (err: unknown) {
      const error = err as { message?: string }
      setError(error?.message || 'Failed to reset password. The link may have expired.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{ background: 'var(--bg-canvas)' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div
          className="rounded-2xl p-8"
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            {/* Diamond Badge */}
            <div
              className="flex items-center justify-center gap-4 mb-6"
              style={{
                color: 'var(--accent)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--tracking-label)',
                fontSize: '0.84rem',
                fontWeight: 600,
              }}
            >
              <DiamondIcon size="md" glow />
              <span>Security</span>
              <DiamondIcon size="md" glow />
            </div>
            <h2
              style={{
                fontFamily: 'var(--font-family-display)',
                fontSize: 'var(--font-size-display-sm)',
                fontWeight: 700,
                lineHeight: 1.12,
                letterSpacing: 'var(--tracking-tight)',
                marginBottom: '0.5rem',
                color: 'var(--text-primary)',
              }}
            >
              Set new{' '}
              <GoldText as="span" glow>
                Password
              </GoldText>
            </h2>
            <p className="text-[var(--text-muted)]" style={{ fontSize: '1rem', lineHeight: 1.6, fontWeight: 400 }}>
              Choose a strong password to secure your account.
            </p>
          </div>

          {/* Success message */}
          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="flex justify-center mb-4">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '2px solid rgba(16, 185, 129, 0.3)',
                  }}
                >
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
                Password reset successful!
              </h3>
              <p className="text-[var(--text-secondary)] mb-6">
                Your password has been changed. Redirecting to login...
              </p>
              <Link
                to="/login"
                className="text-[var(--accent)] hover:underline font-medium"
              >
                Go to login now
              </Link>
            </motion.div>
          ) : (
            <>
              {/* Error message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 rounded-lg flex items-center gap-3"
                  style={{
                    background: 'color-mix(in srgb, var(--state-danger) 10%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--state-danger) 30%, transparent)',
                  }}
                >
                  <AlertCircle className="w-5 h-5 text-[var(--state-danger)] flex-shrink-0" />
                  <p className="text-sm text-[var(--state-danger)]">{error}</p>
                </motion.div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    New password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-muted)]" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-11 pr-11 py-3 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                      placeholder="••••••••"
                      required
                      minLength={8}
                      disabled={isLoading || !token}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    Confirm password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-muted)]" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-11 pr-11 py-3 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                      placeholder="••••••••"
                      required
                      minLength={8}
                      disabled={isLoading || !token}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Password requirements */}
                <div
                  className="p-4 rounded-lg text-xs"
                  style={{
                    background: 'rgba(79, 156, 255, 0.1)',
                    border: '1px solid rgba(79, 156, 255, 0.2)',
                  }}
                >
                  <p className="font-medium text-[var(--text-primary)] mb-2">
                    Password must contain:
                  </p>
                  <ul className="space-y-1 text-[var(--text-secondary)]">
                    <li className={password.length >= 8 ? 'text-green-400' : ''}>
                      • At least 8 characters
                    </li>
                    <li className={/[A-Z]/.test(password) ? 'text-green-400' : ''}>
                      • One uppercase letter
                    </li>
                    <li className={/[a-z]/.test(password) ? 'text-green-400' : ''}>
                      • One lowercase letter
                    </li>
                    <li className={/\d/.test(password) ? 'text-green-400' : ''}>
                      • One number
                    </li>
                    <li className={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) ? 'text-green-400' : ''}>
                      • One special character
                    </li>
                  </ul>
                </div>

                <LuxuryButton
                  type="submit"
                  disabled={isLoading || !token}
                  fullWidth
                  size="md"
                >
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </LuxuryButton>
              </form>

              {/* Help text */}
              <p className="mt-6 text-center text-sm text-[var(--text-muted)]">
                Remember your password?{' '}
                <Link to="/login" className="text-[var(--accent)] hover:underline font-medium">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  )
}

export default ResetPasswordPage
